import React, { useEffect } from 'react';

/**
 * Transaction Completed Gateway Component
 * This component handles payment gateway redirects and communicates with parent window
 * Server redirects to /transaction_completed with payment status and details as URL parameters
 * Component shows only a loader and auto-closes after sending data to parent
 * Works for both courses and services
 */
const TransactionCompleted = () => {
  useEffect(() => {
    const handleTransactionResult = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        
        console.log('[PAYMENT_DEBUG] TransactionCompleted - FULL URL:', window.location.href);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - ALL URL Params:', Object.fromEntries(urlParams.entries()));
        
        // Check if this is for a course or service
        const isServicePayment = window.location.pathname.includes('/student-selfcare/') || 
                                window.location.pathname.includes('/buy-service/');
        const courseId = !isServicePayment ? extractCourseIdFromUrl(window.location.href) : null;
        const serviceType = isServicePayment ? 'selfcare' : null;
        
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Payment type:', isServicePayment ? 'Service' : 'Course');
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Service type:', serviceType);
        
        // Extract payment details from URL parameters
        const status = urlParams.get('status')?.toLowerCase();
        const purchaseId = urlParams.get('purchase_id');
        const transactionId = urlParams.get('transaction_id');
        const orderId = urlParams.get('order_id');
        const gatewayTransactionId = urlParams.get('gateway_transaction_id');
        const amount = urlParams.get('amount');
        const paymentMethod = urlParams.get('payment_method');
        const errorCode = urlParams.get('error_code');
        const errorMessage = urlParams.get('error_message');

        console.log('[PAYMENT_DEBUG] TransactionCompleted - RAW STATUS from backend:', status);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Raw status type:', typeof status);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Error code from backend:', errorCode);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Error message from backend:', errorMessage);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Purchase ID:', purchaseId);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Transaction ID:', transactionId);

        // Determine redirect type based on status
        let redirectType;
        if (status === 'success') {
          redirectType = 'success';
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Status mapping: SUCCESS path taken');
        } else if (status === 'cancelled') {
          redirectType = 'cancelled';
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Status mapping: CANCELLED path taken');
        } else {
          // All other statuses (failed, error, etc.) go to failure page
          redirectType = 'failed';
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Status mapping: FAILED path taken (fallback)');
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Failed because status was:', status);
        }

        console.log('[PAYMENT_DEBUG] TransactionCompleted - FINAL redirectType determined:', redirectType);

        // Prepare payment result data
        const paymentResult = {
          type: 'PAYMENT_RESULT',
          success: status === 'success',
          status: status,
          redirectType: redirectType,
          purchaseId: purchaseId || undefined,
          transactionId: transactionId || gatewayTransactionId || undefined,
          courseId: courseId || undefined,
          serviceType: serviceType || undefined,
          amount: amount ? parseFloat(amount) : undefined,
          paymentMethod: paymentMethod || undefined,
          orderId: orderId || undefined,
          error: status === 'success' ? undefined : (errorMessage || `Payment ${status || 'failed'}`),
          errorCode: errorCode || undefined,
          rawResponse: {
            status,
            purchase_id: purchaseId,
            transaction_id: transactionId,
            order_id: orderId,
            gateway_transaction_id: gatewayTransactionId,
            amount,
            payment_method: paymentMethod,
            error_code: errorCode,
            error_message: errorMessage
          }
        };

        console.log('[PAYMENT_DEBUG] TransactionCompleted - COMPLETE paymentResult object:');
        console.log('[PAYMENT_DEBUG] TransactionCompleted - paymentResult.success:', paymentResult.success);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - paymentResult.status:', paymentResult.status);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - paymentResult.redirectType:', paymentResult.redirectType);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - paymentResult.error:', paymentResult.error);
        console.log('[PAYMENT_DEBUG] TransactionCompleted - Full paymentResult:', paymentResult);

        // Send result to parent window
        if (window.opener && !window.opener.closed) {
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Sending message to parent window');
          window.opener.postMessage(paymentResult, window.location.origin);
          console.log('[PAYMENT_DEBUG] TransactionCompleted - Message sent successfully');
        } else {
          console.log('[PAYMENT_DEBUG] TransactionCompleted - ERROR: No parent window found or parent closed');
        }

        // Close popup after short delay to ensure message is sent
        setTimeout(() => {
          window.close();
        }, 1000); // 1 second delay for user to see "processing" message

      } catch (error) {
        console.error('Error in transaction completed handler:', error);
        
        // Still notify parent of failure and close
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'PAYMENT_RESULT',
            success: false,
            status: 'error',
            redirectType: 'failed',
            error: 'Transaction processing error'
          }, window.location.origin);
        }
        
        setTimeout(() => {
          window.close();
        }, 1000);
      }
    };

    // Execute transaction handling
    handleTransactionResult();
  }, []);

  // ONLY show loader - never show success/failure content
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f8f9fa',
      margin: 0,
      padding: '20px'
    }}>
      {/* Loading Spinner */}
      <div style={{
        width: '50px',
        height: '50px',
        border: '5px solid #e3e3e3',
        borderTop: '5px solid #007bff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      
      {/* Processing Message */}
      <h3 style={{
        color: '#343a40',
        marginBottom: '10px',
        textAlign: 'center'
      }}>
        Processing Transaction...
      </h3>
      
      <p style={{
        color: '#6c757d',
        textAlign: 'center',
        fontSize: '14px',
        margin: 0
      }}>
        Please wait while we process your transaction result.
      </p>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          body {
            margin: 0;
            padding: 0;
            overflow: hidden;
          }
        `}
      </style>
    </div>
  );
};

// Helper function to extract course ID from URL
const extractCourseIdFromUrl = (url: string): string | null => {
  const match = url.match(/\/buy-course\/([^\/]+)\//) || url.match(/course_id=([^&]+)/);
  return match ? match[1] : null;
};

export default TransactionCompleted;